import { FC, useState } from 'react';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { DividendHooks, usePagination } from '../../../../../hooks/index';
import { StringKey } from '../../../../../lang/index';
import { selectedCompanyStorage } from '../../../../../storage/company/index';
import {
  formatDividendAmount,
  formatDividendPerShare,
} from '../../../../dividends/utils/format-dividend-amount.util';
import { formatDividendPeriod } from '../../../../dividends/utils/format-dividend-period.util';
import { EmptyEventHistory } from '../../../empty-event-history';
import { BasicTable } from './basic-table';
import { PaginationItem } from './pagination-item';

const DIVIDEND_HEADERS: StringKey[] = [
  'PAYOUT_DATE',
  'STAKEHOLDER',
  'SHARE_CLASS',
  'PERIOD',
  'DIVIDEND_PER_SHARE',
  'NUMBER_OF_SHARES',
  'TOTAL_PAYOUT',
  'SIGNATURE_STATUS',
];

const ITEMS_PER_PAGE = 10;
const EMPTY_VALUE = '-';

const formatDate = (iso: null | string): string => {
  if (!iso) return EMPTY_VALUE;
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatNumber = (value: null | number): string =>
  value === null ? EMPTY_VALUE : formatDividendPerShare(Number(value));

type Props = { companyId: string };

const cellClass = 'py-3 pl-4 text-sm text-gray-700';

export const Dividend: FC<Props> = ({ companyId }) => {
  const [currentPage, setCurrentPage] = usePagination('events-dividends');
  const [itemsToFetch, setItemsToFetch] = useState(ITEMS_PER_PAGE);

  const company = selectedCompanyStorage(({ company }) => company);
  const currencySymbol = company?.currency?.symbol ?? '$';

  const { records, totalPages, isLoading } = DividendHooks.useDividendStakeholderRows({
    companyId,
    skip: (currentPage - 1) * itemsToFetch,
    take: itemsToFetch,
  });

  if (!isLoading && records.length === 0) {
    return (
      <EmptyEventHistory
        isDisplayedAddEvent={false}
        text={<AppFormattedMessage id="NO_DIVIDEND_PAYOUTS_FOUND" />}
        title={<AppFormattedMessage id="DIVIDENDS" />}
        type={undefined}
      />
    );
  }

  return (
    <>
      <div className="w-full overflow-hidden rounded-md bg-white shadow-sm">
        <BasicTable headers={DIVIDEND_HEADERS}>
          {records.map((row, index) => (
            <tr key={`${row.payoutId}-${row.stakeholderId}-${row.shareClassId ?? index}`}>
              <td className={cellClass}>{formatDate(row.payoutDate)}</td>
              <td className={cellClass}>{row.stakeholderName}</td>
              <td className={cellClass}>{row.shareClassName ?? EMPTY_VALUE}</td>
              <td className={cellClass}>
                {formatDividendPeriod(row.frequency, row.year, row.periodIndex)}
              </td>
              <td className={cellClass}>{formatNumber(row.dividendPerShare)}</td>
              <td className={cellClass}>{formatNumber(row.shares)}</td>
              <td className={cellClass}>
                {`${currencySymbol} ${formatDividendAmount(Number(row.totalAmount))}`}
              </td>
              <td className={cellClass}>{EMPTY_VALUE}</td>
            </tr>
          ))}
        </BasicTable>
      </div>
      <PaginationItem
        currentPage={currentPage}
        handleChangeCurrentPage={setCurrentPage}
        handleFetchAmountChange={setItemsToFetch}
        itemsCount={records.length}
        itemsToFetch={itemsToFetch}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
